import { Bot, CalendarCheck, IndianRupee, LogIn, UserRound, Users } from "lucide-react";
import { PROPERTY_TIME_ZONE } from "@/lib/format";

// One entry of "who did what" (GET /bookings/:id/history and /reports/activity).
export type ActivityEntry = {
  id: string;
  at: string;
  category: "BOOKING" | "STAY" | "MONEY" | "GUESTS";
  action: string;
  title: string;
  detail: string | null;
  reason: string | null;
  amount: number | null;
  actor: { type: string; id: string | null; name: string };
  booking: { id: string; bookingRef: string; guestName: string };
};

const CATEGORY_ICON = { BOOKING: CalendarCheck, STAY: LogIn, MONEY: IndianRupee, GUESTS: Users } as const;

const dayLabel = (iso: string) => new Intl.DateTimeFormat("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric", timeZone: PROPERTY_TIME_ZONE }).format(new Date(iso));
const timeLabel = (iso: string) => new Intl.DateTimeFormat("en-IN", { hour: "numeric", minute: "2-digit", timeZone: PROPERTY_TIME_ZONE }).format(new Date(iso));

// Timeline grouped by day, newest first. `showBooking` adds the booking ref / guest (property-wide log).
export function ActivityList({ entries, showBooking = false, onOpenBooking }: { entries: ActivityEntry[]; showBooking?: boolean; onOpenBooking?: (bookingId: string) => void }) {
  const days: { day: string; items: ActivityEntry[] }[] = [];
  for (const entry of entries) {
    const day = dayLabel(entry.at);
    const last = days.at(-1);
    if (last?.day === day) last.items.push(entry);
    else days.push({ day, items: [entry] });
  }
  return (
    <div className="activity-list">
      {days.map(({ day, items }) => (
        <section key={day} className="activity-day">
          <h5 className="activity-day__label">{day}</h5>
          <ol>
            {items.map((entry) => {
              const Icon = CATEGORY_ICON[entry.category] ?? CalendarCheck;
              const ActorIcon = entry.actor.type === "ADAPTER" || entry.actor.type === "SYSTEM" ? Bot : UserRound;
              return (
                <li key={entry.id} className={`activity-item activity-item--${entry.category.toLowerCase()}`}>
                  <span className="activity-item__icon"><Icon size={15} aria-hidden="true" /></span>
                  <div className="activity-item__body">
                    <strong>{entry.title}</strong>
                    {entry.detail && <small>{entry.detail}</small>}
                    {entry.reason && <small className="activity-item__reason">Reason: {entry.reason}</small>}
                    <span className="activity-item__meta">
                      <ActorIcon size={12} aria-hidden="true" /> {entry.actor.name} · {timeLabel(entry.at)}
                      {showBooking && (
                        <> · {onOpenBooking
                          ? <button type="button" className="text-link" onClick={() => onOpenBooking(entry.booking.id)}>{entry.booking.bookingRef}</button>
                          : entry.booking.bookingRef} ({entry.booking.guestName})</>
                      )}
                    </span>
                  </div>
                </li>
              );
            })}
          </ol>
        </section>
      ))}
    </div>
  );
}
